import { memo, ReactNode } from 'react';
import { Box, Chip, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import NumbersIcon from '@mui/icons-material/Numbers';
import NotesIcon from '@mui/icons-material/Notes';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useTranslation } from 'react-i18next';
import {
  IProductTemplateField,
  PRODUCT_TEMPLATE_FIELD_TYPE,
} from 'modules/inventory/settings/product-templates/interfaces';

type ProductTemplatePreviewProps = {
  name?: string;
  fields?: IProductTemplateField[];
  bordered?: boolean;
};

const TYPE_ICON: Record<PRODUCT_TEMPLATE_FIELD_TYPE, ReactNode> = {
  [PRODUCT_TEMPLATE_FIELD_TYPE.STRING]: <NotesIcon fontSize='small' />,
  [PRODUCT_TEMPLATE_FIELD_TYPE.NUMBER]: <NumbersIcon fontSize='small' />,
  [PRODUCT_TEMPLATE_FIELD_TYPE.FILE]: <UploadFileIcon fontSize='small' />,
  [PRODUCT_TEMPLATE_FIELD_TYPE.CI]: <BadgeOutlinedIcon fontSize='small' />,
};

const PreviewField = ({ field }: { field: IProductTemplateField }) => {
  const { t } = useTranslation('productTemplates');
  const label = field.name || t('preview.unnamedField');

  if (field.field === PRODUCT_TEMPLATE_FIELD_TYPE.FILE) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          color: 'text.secondary',
          bgcolor: 'action.hover',
        }}
      >
        <UploadFileIcon />
        <Box>
          <Typography variant='body2' color='text.primary'>
            {label}
            {field.required ? ' *' : ''}
          </Typography>
          <Typography variant='caption'>{t('preview.upload')}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <TextField
      disabled
      fullWidth
      size='small'
      label={label}
      required={field.required}
      type={field.field === PRODUCT_TEMPLATE_FIELD_TYPE.NUMBER ? 'number' : 'text'}
      InputProps={{
        startAdornment: <InputAdornment position='start'>{TYPE_ICON[field.field]}</InputAdornment>,
        endAdornment: (
          <InputAdornment position='end'>
            <Chip size='small' variant='outlined' label={t(`FIELD_TYPE.${field.field}`)} />
          </InputAdornment>
        ),
      }}
    />
  );
};

const ProductTemplatePreview = ({ name, fields, bordered = true }: ProductTemplatePreviewProps) => {
  const { t } = useTranslation('productTemplates');
  const sorted = fields || [];

  return (
    <Box
      sx={{
        p: 2.5,
        borderRadius: 2,
        ...(bordered && { border: 1, borderColor: 'divider', bgcolor: 'background.paper' }),
      }}
    >
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' spacing={1} mb={2.5}>
        <Box>
          <Typography variant='overline' color='text.secondary' lineHeight={1.5}>
            {t('preview.title')}
          </Typography>
          <Typography variant='h6' fontWeight={600}>
            {name || '-'}
          </Typography>
        </Box>
        <Chip size='small' color='primary' variant='outlined' label={`${sorted.length} ${t('fields.fieldsCount')}`} />
      </Stack>
      {sorted.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          {t('emptyFields')}
        </Typography>
      ) : (
        <Stack spacing={2}>
          {sorted.map((field, index) => (
            <PreviewField key={`${index}-${field.name}`} field={field} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default memo(ProductTemplatePreview);
